import { useCallback } from 'react';

import { useTelemetry, useTrackEventEffect } from '@order/Telemetry';

export const useScanToPayTelemetry = (props: UseScanToPayTelemetryProps) => {
  const {
    isPremium,
    isOnScanAndPayTab,
    goToSgRewards,
    goToForgotToScan,
    goToSupport,
    requestAddPayment,
    onPaymentMethodAdded,
    requestPaymentSelection,
    switchPaymentMethod,
    switchUseCredit,
    requestLoyaltyInfo,
    requestFaq,
  } = props;

  const { track } = useTelemetry();

  const loyaltyTier = isPremium ? 'premium' : 'sg rewards';

  useTrackEventEffect({
    name: 'scan_to_pay.scan-and-pay.viewed',
    payload: { loyaltyTier },
    skip: !isOnScanAndPayTab,
  });

  useTrackEventEffect({
    name: 'scan_to_pay.scan-only.viewed',
    payload: { loyaltyTier },
    skip: isOnScanAndPayTab,
  });

  const pointsTapped = useCallback(() => {
    track('scan_to_pay.points.tapped', { loyaltyTier });
    requestLoyaltyInfo();
  }, [track, loyaltyTier, requestLoyaltyInfo]);

  const goToRewardsTapped = useCallback(() => {
    track('scan_to_pay.go-to-rewards.tapped', { loyaltyTier });
    goToSgRewards();
  }, [track, loyaltyTier, goToSgRewards]);

  const faqTapped = useCallback(() => {
    track('scan_to_pay.faq.tapped', { loyaltyTier });
    requestFaq();
  }, [track, loyaltyTier, requestFaq]);

  const contactUsTapped = useCallback(() => {
    track('scan_to_pay.contact-us.tapped', { loyaltyTier });
    goToSupport();
  }, [track, loyaltyTier, goToSupport]);

  const forgotToScanTapped = useCallback(() => {
    track('forgot-to-scan.cta_tapped', { location: 'scan_to_pay' });
    goToForgotToScan();
  }, [track, goToForgotToScan]);

  const addCardTapped = useCallback(() => {
    track('scan_to_pay.add-card.tapped', { loyaltyTier });
    requestAddPayment();
  }, [track, loyaltyTier, requestAddPayment]);

  const addCardSuccess = useCallback(() => {
    track('scan_to_pay.add-card.success', { loyaltyTier });
    onPaymentMethodAdded();
  }, [track, loyaltyTier, onPaymentMethodAdded]);

  const onSwitchUseCredit = useCallback(
    (useCredit: boolean) => {
      track(useCredit ? 'scan_to_pay.credit.enabled' : 'scan_to_pay.credit.disabled', {
        loyaltyTier,
      });

      switchUseCredit(useCredit);
    },
    [track, loyaltyTier, switchUseCredit],
  );

  const changePaymentTapped = useCallback(() => {
    track('scan_to_pay.change-payment.tapped', { loyaltyTier });
    requestPaymentSelection();
  }, [track, loyaltyTier, requestPaymentSelection]);

  const changePaymentAddCardTapped = useCallback(() => {
    track('scan_to_pay.change-payment.add-card.tapped', { loyaltyTier });
    requestAddPayment();
  }, [track, loyaltyTier, requestAddPayment]);

  const changePaymentSuccess = useCallback(
    (pmId: string) => {
      track('scan_to_pay.change-payment.success', { loyaltyTier });
      switchPaymentMethod(pmId);
    },
    [track, loyaltyTier, switchPaymentMethod],
  );

  return {
    pointsTapped,
    goToRewardsTapped,
    faqTapped,
    contactUsTapped,
    forgotToScanTapped,
    addCardTapped,
    addCardSuccess,
    onSwitchUseCredit,
    changePaymentTapped,
    changePaymentAddCardTapped,
    changePaymentSuccess,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScanToPayTelemetryProps = Readonly<{
  isPremium: boolean;
  isOnScanAndPayTab: boolean;
  goToSgRewards: () => void;
  goToForgotToScan: () => void;
  goToSupport: () => void;
  requestAddPayment: () => void;
  onPaymentMethodAdded: () => void;
  requestPaymentSelection: () => void;
  switchPaymentMethod: (pmId: string) => void;
  switchUseCredit: (useCredit: boolean) => void;
  requestLoyaltyInfo: () => void;
  requestFaq: () => void;
}>;
